import type { IPlaylistDB } from "../../interfaces/Playlist";
import type { IStudioWidgetPlaylistRow } from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

export const DEFAULT_LIVE_PREFIX_FORMAT = "{number}";
export const DEFAULT_LIVE_TITLE_FORMAT = "{name}";
export const DEFAULT_LIVE_SEPARATOR = "→";

/**
 * Replace `{name}`, `{artist}`, `{album}`, `{number}`, `{index}` placeholders
 * in a format string with values from a `IPlaylistDB` row. Unknown placeholders
 * are left untouched (useful for diagnosing typos at preview time).
 */
const interpolate = (
  template: string,
  track: IPlaylistDB,
  index: number,
): string => {
  return template
    .replace(/\{name\}/g, track.name ?? "")
    .replace(/\{artist\}/g, track.artist?.name ?? "")
    .replace(/\{album\}/g, track.album?.name ?? "")
    .replace(/\{number\}/g, String(track.number ?? index + 1))
    .replace(/\{index\}/g, String(index + 1));
};

/**
 * Convert the live Playlist DB tracks into the row shape the Playlist
 * widget renders (prefix / separator / title). Pure — both the DOM and
 * Pixi renderers call this and feed the result into their existing
 * row-rendering paths, so a single set of format string semantics applies
 * everywhere. Empty / missing track list → empty rows array (renderer
 * paints nothing).
 */
export const buildLivePlaylistRows = (
  tracks: IPlaylistDB[] | undefined | null,
  options: {
    prefixFormat?: string;
    titleFormat?: string;
    separator?: string;
    maxRows?: number;
  } = {},
): IStudioWidgetPlaylistRow[] => {
  if (!tracks || tracks.length === 0) return [];

  const prefixFormat = options.prefixFormat ?? DEFAULT_LIVE_PREFIX_FORMAT;
  const titleFormat = options.titleFormat ?? DEFAULT_LIVE_TITLE_FORMAT;
  const separator = options.separator ?? DEFAULT_LIVE_SEPARATOR;
  const cap =
    options.maxRows && options.maxRows > 0
      ? Math.min(options.maxRows, tracks.length)
      : tracks.length;

  const out: IStudioWidgetPlaylistRow[] = [];
  for (let i = 0; i < cap; i++) {
    const track = tracks[i];
    out.push({
      prefix: interpolate(prefixFormat, track, i),
      separator,
      title: interpolate(titleFormat, track, i),
    });
  }
  return out;
};

/** Format a track duration (seconds) as "m:ss"; empty when missing/0. */
export const formatTrackDuration = (seconds: number | undefined): string => {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return "";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}`;
  return `${m}:${ss}`;
};

/**
 * Build the rows a design-store card shows: the same title interpolation as
 * {@link buildLivePlaylistRows} plus a formatted duration (from the track's
 * runtime metadata, empty when unknown). Used by the Playlist design renderer.
 */
export const buildPlaylistDesignRows = (
  tracks: IPlaylistDB[] | undefined | null,
  options: {
    titleFormat?: string;
    prefixFormat?: string;
    maxRows?: number;
  } = {},
): { title: string; duration: string }[] => {
  const rows = buildLivePlaylistRows(tracks, options);
  return rows.map((r, i) => ({
    title: r.title,
    duration: formatTrackDuration(tracks?.[i]?.metadata?.duration),
  }));
};
